import React from 'react';
import { FaPlus, FaTrash, FaInfoCircle, FaGripVertical } from 'react-icons/fa';
import CatalogItemPicker from './CatalogItemPicker';
import CatalogService, { CatalogItem, CatalogItemKind } from '../services/CatalogService';
import { MilestoneInfo } from '../services/OfferService';
import NumericInput from '../../../components/NumericInput';

export interface CatalogMeta {
  sku: string;
  kind: CatalogItemKind;
  purchase_price: number | null;
}

export interface LineItemForm {
  id?: number;
  position: number;
  catalog_item_id?: number;
  name: string;
  description: string;
  quantity: string;
  unit: string;
  unit_price: string;
  tax_rate: string;
  discount_percent: string;
  _catalogMeta?: CatalogMeta;
  scope_items_count?: number;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  fontSize: '14px',
  boxSizing: 'border-box',
};

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: 600,
  color: '#666',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: '14px',
  color: '#333',
  verticalAlign: 'top',
};

interface OfferLineItemsTableProps {
  lineItems: LineItemForm[];
  shouldShowMilestoneSections: boolean;
  milestonesFromAssignments: MilestoneInfo[];
  draggedLineIndex: number | null;
  dragOverMilestoneId: number | null | 'unassigned';
  savingLineId: number | null;
  totals: { subtotalNet: number; totalTax: number; totalGross: number };
  onAddLine: () => void;
  onRemoveLine: (index: number) => void;
  onLineChange: (index: number, field: keyof LineItemForm, value: string | number) => void;
  onCatalogSelect: (index: number, catalogItem: CatalogItem) => void;
  onMarkupChange: (index: number, markupValue: string) => void;
  onScopeDrawerOpen: (line: { id: number; name: string }) => void;
  onLineDragStart: (e: React.DragEvent, index: number, item: LineItemForm) => void;
  onLineDragEnd: () => void;
  onMilestoneDragOver: (e: React.DragEvent, milestoneId: number | 'unassigned') => void;
  onMilestoneDragLeave: () => void;
  onMilestoneDrop: (e: React.DragEvent, targetMilestoneId: number | null) => void;
  onMilestoneAssignmentChange: (lineId: number | undefined, milestoneId: number | null) => void;
  onPendingMilestoneChange: (index: number, milestoneId: number | null) => void;
  isLineEmpty: (item: LineItemForm) => boolean;
  calculateLineTotal: (item: LineItemForm) => { net: number; gross: number };
  calculateMarkup: (item: LineItemForm) => string;
  getPurchasePrice: (item: LineItemForm) => number | null;
  hasCatalogPurchasePrice: (item: LineItemForm) => boolean;
  getOfferLineId: (row: LineItemForm | any) => number | null;
  getMilestoneForLine: (index: number, lineId: number | null) => number | null;
  getLinesForMilestone: (milestoneId: number) => Array<{ line: LineItemForm; index: number }>;
  getUnassignedLines: () => Array<{ line: LineItemForm; index: number }>;
  calculateMilestoneSectionTotal: (milestoneId: number) => number;
  getMilestoneStatusColor: (status: string) => { bg: string; text: string };
  formatCurrency: (amount: number) => string;
}

export default function OfferLineItemsTable({
  lineItems,
  shouldShowMilestoneSections,
  milestonesFromAssignments,
  draggedLineIndex,
  dragOverMilestoneId,
  savingLineId,
  totals,
  onAddLine,
  onRemoveLine,
  onLineChange,
  onCatalogSelect,
  onMarkupChange,
  onScopeDrawerOpen,
  onLineDragStart,
  onLineDragEnd,
  onMilestoneDragOver,
  onMilestoneDragLeave,
  onMilestoneDrop,
  onMilestoneAssignmentChange,
  onPendingMilestoneChange,
  isLineEmpty,
  calculateLineTotal,
  calculateMarkup,
  getPurchasePrice,
  hasCatalogPurchasePrice,
  getOfferLineId,
  getMilestoneForLine,
  getLinesForMilestone,
  getUnassignedLines,
  calculateMilestoneSectionTotal,
  getMilestoneStatusColor,
  formatCurrency,
}: OfferLineItemsTableProps): React.JSX.Element {

  const renderLineRow = (
    item: LineItemForm,
    index: number,
    options: {
      showDragHandle: boolean;
      showMilestoneColumn: boolean;
      milestoneId?: number | 'unassigned';
      bgHighlight?: string;
    }
  ) => {
    const lineTotal = calculateLineTotal(item);
    const isDragging = draggedLineIndex === index;
    const offerLineId = getOfferLineId(item);
    const isRealLine = offerLineId !== null;

    const dragOverBg = options.milestoneId !== undefined
      ? (options.milestoneId === 'unassigned'
        ? (dragOverMilestoneId === 'unassigned' ? '#fff5f5' : '#fff')
        : (dragOverMilestoneId === options.milestoneId ? '#f0f4ff' : '#fff'))
      : '#fff';

    const draggingBg = options.milestoneId === 'unassigned' ? '#fee2e2' : '#e0e7ff';

    return (
      <tr
        key={isRealLine ? `line-${offerLineId}` : `new-${index}`}
        data-testid={isRealLine ? `offer-line-row-${offerLineId}` : `offer-line-row-new-${index}`}
        onDragOver={options.milestoneId !== undefined ? (e) => handleRowDragOver(e, options.milestoneId!) : undefined}
        onDragLeave={options.milestoneId !== undefined ? onMilestoneDragLeave : undefined}
        onDrop={options.milestoneId !== undefined ? (e) => onMilestoneDrop(e, options.milestoneId === 'unassigned' ? null : (options.milestoneId as number)) : undefined}
        style={{
          borderBottom: '1px solid #e0e3ef',
          background: isDragging ? draggingBg : dragOverBg,
          opacity: savingLineId === offerLineId ? 0.6 : 1,
        }}
      >
        {options.showDragHandle && (
          <td style={{ ...tdStyle, width: '28px' }}>
            <div
              draggable={isRealLine}
              onDragStart={(e) => {
                e.stopPropagation();
                if (!isRealLine) return;
                e.dataTransfer.effectAllowed = 'move';
                onLineDragStart(e, index, item);
              }}
              onDragEnd={onLineDragEnd}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: 4,
                cursor: isRealLine ? 'grab' : 'default',
                userSelect: 'none',
                position: 'relative',
                zIndex: 100,
              }}
              title={isRealLine ? 'Ziehen, um Position einem Milestone zuzuordnen' : 'Speichere zuerst die Position'}
              data-testid={isRealLine ? `offer-line-drag-handle-${offerLineId}` : `offer-line-drag-handle-new-${index}`}
            >
              <FaGripVertical size={14} color={isRealLine ? '#6366f1' : '#d1d5db'} style={{ pointerEvents: 'none' }} />
            </div>
          </td>
        )}
        <td style={tdStyle}>{index + 1}</td>
        <td style={{ ...tdStyle, fontSize: '12px', color: '#666' }}>
          {item._catalogMeta?.sku || '-'}
        </td>
        <td style={{ ...tdStyle, fontSize: '12px' }}>
          {item._catalogMeta?.kind ? (
            <span style={{ display: 'inline-block', padding: '2px 6px', background: '#f0f4ff', borderRadius: '4px', fontSize: '11px', color: '#021a4a' }}>
              {CatalogService.getKindLabel(item._catalogMeta.kind)}
            </span>
          ) : '-'}
        </td>
        <td style={{ ...tdStyle, minWidth: '280px' }}>
          {item.name ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span
                onClick={() => isRealLine && onScopeDrawerOpen({ id: offerLineId!, name: item.name })}
                style={{
                  fontWeight: 600,
                  color: '#021a4a',
                  cursor: isRealLine ? 'pointer' : 'default',
                  textDecoration: isRealLine ? 'underline' : 'none',
                  textDecorationStyle: 'dotted',
                  textUnderlineOffset: '3px',
                }}
                title={isRealLine ? 'Klicken für Details & Subtasks' : undefined}
              >
                {item.name}
              </span>
              <CatalogItemPicker onSelect={(catalogItem) => onCatalogSelect(index, catalogItem)} placeholder="Artikel suchen..." compact />
            </div>
          ) : (
            <CatalogItemPicker onSelect={(catalogItem) => onCatalogSelect(index, catalogItem)} placeholder="Artikel auswählen..." />
          )}
        </td>
        <td style={{ ...tdStyle, width: '80px' }}>
          <NumericInput value={item.quantity} onChange={(val) => onLineChange(index, 'quantity', val)} decimals={2} placeholder="1.00" min={0} style={{ padding: '6px 8px', fontSize: '13px', minWidth: '70px' }} />
        </td>
        <td style={{ ...tdStyle, width: '70px' }}>
          <input type="text" value={item.unit} onChange={(e) => onLineChange(index, 'unit', e.target.value)} style={{ ...inputStyle, padding: '6px 8px', fontSize: '13px', width: '55px' }} />
        </td>
        <td style={{ ...tdStyle, width: '100px', textAlign: 'right', color: hasCatalogPurchasePrice(item) ? '#888' : '#aaa', background: '#fafafa', fontSize: '13px', fontStyle: hasCatalogPurchasePrice(item) ? 'normal' : 'italic', whiteSpace: 'nowrap' }} title={hasCatalogPurchasePrice(item) ? "Based on catalog purchase price" : "Estimated (70% of unit price)"}>
          {getPurchasePrice(item) != null ? formatCurrency(getPurchasePrice(item)!) : '-'}
          {!hasCatalogPurchasePrice(item) && getPurchasePrice(item) != null && <span style={{ fontSize: '10px', marginLeft: '2px' }}>*</span>}
        </td>
        <td style={{ ...tdStyle, width: '80px', background: '#fafafa' }}>
          {getPurchasePrice(item) != null ? (
            <NumericInput value={calculateMarkup(item)} onChange={(val) => onMarkupChange(index, val)} decimals={2} placeholder="1.00" min={0} style={{ padding: '6px 8px', fontSize: '13px', minWidth: '65px' }} />
          ) : (
            <span style={{ color: '#888', fontSize: '13px' }}>-</span>
          )}
        </td>
        <td style={{ ...tdStyle, width: '110px' }}>
          <NumericInput value={item.unit_price} onChange={(val) => onLineChange(index, 'unit_price', val)} decimals={2} placeholder="0.00" min={0} style={{ padding: '6px 8px', fontSize: '13px', minWidth: '95px' }} />
        </td>
        <td style={{ ...tdStyle, width: '70px' }}>
          <NumericInput value={item.tax_rate} onChange={(val) => onLineChange(index, 'tax_rate', val)} decimals={2} placeholder="19.00" min={0} max={100} style={{ padding: '6px 8px', fontSize: '13px', minWidth: '55px' }} />
        </td>
        <td style={{ ...tdStyle, width: '70px' }}>
          <NumericInput value={item.discount_percent} onChange={(val) => onLineChange(index, 'discount_percent', val)} decimals={2} placeholder="0.00" min={0} max={100} style={{ padding: '6px 8px', fontSize: '13px', minWidth: '55px' }} />
        </td>
        {/* Scope column */}
        <td style={{ ...tdStyle, width: '80px', textAlign: 'center' }}>
          {isRealLine ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              <span
                data-testid={`offer-line-scope-count-${offerLineId}`}
                style={{
                  fontSize: '12px',
                  color: (item.scope_items_count ?? 0) > 0 ? '#166534' : '#9ca3af',
                  fontWeight: (item.scope_items_count ?? 0) > 0 ? 600 : 400,
                }}
              >
                {(item.scope_items_count ?? 0) > 0 ? `🧾 ${item.scope_items_count}` : '—'}
              </span>
              <button
                data-testid={`offer-line-scope-open-${offerLineId}`}
                onClick={() => onScopeDrawerOpen({ id: offerLineId!, name: item.name })}
                title="Scope öffnen"
                style={{
                  background: 'none',
                  border: '1px solid #e0e3ef',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  cursor: 'pointer',
                  fontSize: '10px',
                  color: '#666',
                }}
              >
                ✎
              </button>
            </div>
          ) : (
            <span style={{ color: '#ccc', fontSize: '12px' }}>—</span>
          )}
        </td>
        {options.showMilestoneColumn && (
          <td style={{ ...tdStyle, width: '140px' }}>
            <select
              data-testid={isRealLine ? `offer-line-milestone-select-${offerLineId}` : `offer-line-milestone-select-new-${index}`}
              value={getMilestoneForLine(index, offerLineId) ?? ''}
              onChange={(e) => {
                const milestoneIdVal = e.target.value ? parseInt(e.target.value, 10) : null;
                if (isRealLine) {
                  onMilestoneAssignmentChange(offerLineId, milestoneIdVal);
                } else {
                  onPendingMilestoneChange(index, milestoneIdVal);
                }
              }}
              disabled={savingLineId === offerLineId}
              style={{ ...inputStyle, padding: '6px 8px', fontSize: '12px', width: '130px' }}
            >
              <option value="">Nicht zugeordnet</option>
              {milestonesFromAssignments.map((m) => (
                <option key={m.id} value={m.id}>{m.label} ({m.percentage}%)</option>
              ))}
            </select>
          </td>
        )}
        <td style={{ ...tdStyle, width: '120px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {formatCurrency(lineTotal.net)}
        </td>
        <td style={tdStyle}>
          <button onClick={() => onRemoveLine(index)} title="Remove item" style={{ background: 'none', border: 'none', padding: '6px', cursor: 'pointer', color: '#dc2626', borderRadius: '4px' }}>
            <FaTrash size={14} />
          </button>
        </td>
      </tr>
    );
  };

  const handleRowDragOver = (e: React.DragEvent, milestoneId: number | 'unassigned') => {
    onMilestoneDragOver(e, milestoneId);
  };

  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e0e3ef',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
          Line items
        </h3>
        <button
          onClick={onAddLine}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            border: 'none',
            borderRadius: '6px',
            background: '#021a4a',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <FaPlus size={12} />
          Add line item
        </button>
      </div>

      {/* Unassigned positions hint */}
      {shouldShowMilestoneSections && getUnassignedLines().length > 0 && (
        <div
          data-testid="offer-unassigned-hint"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            marginBottom: '12px',
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
            color: '#92400e',
            fontSize: '14px',
          }}
        >
          <FaInfoCircle size={16} />
          <span>
            <strong>{getUnassignedLines().length} Position{getUnassignedLines().length !== 1 ? 'en' : ''}</strong> {getUnassignedLines().length === 1 ? 'ist' : 'sind'} noch keinem Milestone zugeordnet.
            Ziehe sie per Drag &amp; Drop in die Abschnitte oder nutze das Dropdown pro Zeile.
          </span>
        </div>
      )}

      <div
        style={{ overflowX: 'auto' }}
        onDragOver={(e) => {
          e.preventDefault();
          const scrollThreshold = 80;
          const scrollSpeed = 15;
          const mouseY = e.clientY;
          const viewportHeight = window.innerHeight;

          if (mouseY < scrollThreshold) {
            window.scrollBy({ top: -scrollSpeed, behavior: 'auto' });
          } else if (mouseY > viewportHeight - scrollThreshold) {
            window.scrollBy({ top: scrollSpeed, behavior: 'auto' });
          }
        }}
      >
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
            <thead>
              <tr style={{ background: '#f8f9fc', borderBottom: '2px solid #e0e3ef' }}>
                {shouldShowMilestoneSections && <th style={{ ...thStyle, width: '30px' }}></th>}
                <th style={{ ...thStyle, width: '40px' }}>#</th>
                <th style={{ ...thStyle, width: '80px' }}>SKU</th>
                <th style={{ ...thStyle, width: '80px' }}>Category</th>
                <th style={{ ...thStyle, minWidth: '250px' }}>Item</th>
                <th style={{ ...thStyle, width: '80px', textAlign: 'right' }}>Qty</th>
                <th style={{ ...thStyle, width: '70px' }}>Unit</th>
                <th style={{ ...thStyle, width: '100px', textAlign: 'right' }}>
                  <span title="Based on catalog purchase price - not shown on PDF" style={{ cursor: 'help' }}>
                    Cost (net) <FaInfoCircle size={10} style={{ opacity: 0.6 }} />
                  </span>
                </th>
                <th style={{ ...thStyle, width: '80px', textAlign: 'right' }}>
                  <span title="Markup factor: Unit price / Cost" style={{ cursor: 'help' }}>
                    Factor
                  </span>
                </th>
                <th style={{ ...thStyle, width: '110px', textAlign: 'right' }}>Unit price</th>
                <th style={{ ...thStyle, width: '70px', textAlign: 'right' }}>VAT %</th>
                <th style={{ ...thStyle, width: '70px', textAlign: 'right' }}>Disc %</th>
                <th style={{ ...thStyle, width: '80px', textAlign: 'center' }}>Scope</th>
                {shouldShowMilestoneSections && <th style={{ ...thStyle, width: '140px' }}>Milestone</th>}
                <th style={{ ...thStyle, width: '120px', textAlign: 'right' }}>Net total</th>
                <th style={{ ...thStyle, width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {shouldShowMilestoneSections ? (
                <>
                  {/* Milestone sections */}
                  {milestonesFromAssignments.map((milestone) => {
                    const milestoneLines = getLinesForMilestone(milestone.id);
                    const sectionTotal = calculateMilestoneSectionTotal(milestone.id);
                    const statusColor = getMilestoneStatusColor(milestone.status);
                    const isDragOver = dragOverMilestoneId === milestone.id;

                    return (
                      <React.Fragment key={`milestone-${milestone.id}`}>
                        {/* Milestone Section Header Row */}
                        <tr
                          data-testid={`milestone-section-${milestone.id}`}
                          style={{
                            background: isDragOver ? '#e0e7ff' : '#f0f4ff',
                            borderBottom: '2px solid #c7d2fe',
                            transition: 'background 0.15s',
                          }}
                        >
                          <td
                            colSpan={shouldShowMilestoneSections ? 16 : 14}
                            style={{
                              padding: '10px 16px',
                              position: 'relative',
                              border: isDragOver ? '2px dashed #4f46e5' : 'none',
                            }}
                          >
                            {/* Drop overlay */}
                            <div
                              data-testid={`milestone-dropzone-${milestone.id}`}
                              onDragOver={(e) => onMilestoneDragOver(e, milestone.id)}
                              onDragLeave={onMilestoneDragLeave}
                              onDrop={(e) => onMilestoneDrop(e, milestone.id)}
                              style={{
                                position: 'absolute',
                                inset: 0,
                                pointerEvents: draggedLineIndex !== null ? 'auto' : 'none',
                                zIndex: 1,
                              }}
                            />
                            {/* Header content */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontWeight: 700, color: '#021a4a', fontSize: '14px' }}>
                                  {milestone.label}
                                </span>
                                <span
                                  style={{
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    background: statusColor.bg,
                                    color: statusColor.text,
                                  }}
                                >
                                  {milestone.status}
                                </span>
                                <span style={{ fontSize: '13px', color: '#666' }}>{milestone.percentage}%</span>
                              </div>
                              <div style={{ fontSize: '13px', color: '#666' }}>
                                {milestoneLines.length} Position{milestoneLines.length !== 1 ? 'en' : ''} · {formatCurrency(sectionTotal)}
                              </div>
                            </div>
                          </td>
                        </tr>
                        {/* Lines for this milestone */}
                        {milestoneLines.map(({ line: item, index }) =>
                          renderLineRow(item, index, {
                            showDragHandle: true,
                            showMilestoneColumn: true,
                            milestoneId: milestone.id,
                          })
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* Unassigned Section */}
                  {(() => {
                    const unassignedLines = getUnassignedLines();
                    const isDragOver = dragOverMilestoneId === 'unassigned';
                    if (unassignedLines.length === 0) {
                      return null;
                    }
                    return (
                      <>
                        <tr
                          data-testid="milestone-section-unassigned"
                          onDragOver={(e) => onMilestoneDragOver(e, 'unassigned')}
                          onDragLeave={onMilestoneDragLeave}
                          onDrop={(e) => onMilestoneDrop(e, null)}
                          style={{
                            background: isDragOver ? '#fee2e2' : '#fff5f5',
                            borderBottom: '2px solid #fecaca',
                            transition: 'background 0.15s',
                          }}
                        >
                          <td
                            data-testid="milestone-dropzone-unassigned"
                            colSpan={shouldShowMilestoneSections ? 16 : 14}
                            style={{
                              padding: '10px 16px',
                              border: isDragOver ? '2px dashed #dc2626' : 'none',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FaInfoCircle size={14} color="#dc2626" />
                                <span style={{ fontWeight: 700, color: '#dc2626', fontSize: '14px' }}>Nicht zugeordnet</span>
                              </div>
                              <span style={{ fontSize: '13px', color: '#666' }}>
                                {unassignedLines.length} Position{unassignedLines.length !== 1 ? 'en' : ''}
                              </span>
                            </div>
                          </td>
                        </tr>
                        {unassignedLines.map(({ line: item, index }) =>
                          renderLineRow(item, index, {
                            showDragHandle: true,
                            showMilestoneColumn: true,
                            milestoneId: 'unassigned',
                          })
                        )}
                      </>
                    );
                  })()}

                  {/* Empty line for adding new items */}
                  {lineItems.filter(isLineEmpty).map((item, idx) => {
                    const originalIndex = lineItems.indexOf(item);
                    return (
                      <tr key={`empty-${idx}`} style={{ borderBottom: '1px solid #e0e3ef', background: '#fafafa' }}>
                        <td style={{ ...tdStyle, width: '30px' }}></td>
                        <td style={tdStyle}>{originalIndex + 1}</td>
                        <td style={{ ...tdStyle, fontSize: '12px', color: '#666' }}>-</td>
                        <td style={{ ...tdStyle, fontSize: '12px' }}>-</td>
                        <td style={{ ...tdStyle, minWidth: '280px' }}>
                          <CatalogItemPicker onSelect={(catalogItem) => onCatalogSelect(originalIndex, catalogItem)} placeholder="Artikel auswählen..." />
                        </td>
                        <td style={{ ...tdStyle, width: '80px' }}>
                          <NumericInput value={item.quantity} onChange={(val) => onLineChange(originalIndex, 'quantity', val)} decimals={2} placeholder="1.00" min={0} style={{ padding: '6px 8px', fontSize: '13px', minWidth: '70px' }} />
                        </td>
                        <td style={{ ...tdStyle, width: '70px' }}>
                          <input type="text" value={item.unit} onChange={(e) => onLineChange(originalIndex, 'unit', e.target.value)} style={{ ...inputStyle, padding: '6px 8px', fontSize: '13px', width: '55px' }} />
                        </td>
                        <td style={{ ...tdStyle, width: '100px', textAlign: 'right', background: '#fafafa' }}>-</td>
                        <td style={{ ...tdStyle, width: '80px', background: '#fafafa' }}>-</td>
                        <td style={{ ...tdStyle, width: '110px' }}>
                          <NumericInput value={item.unit_price} onChange={(val) => onLineChange(originalIndex, 'unit_price', val)} decimals={2} placeholder="0.00" min={0} style={{ padding: '6px 8px', fontSize: '13px', minWidth: '95px' }} />
                        </td>
                        <td style={{ ...tdStyle, width: '70px' }}>
                          <NumericInput value={item.tax_rate} onChange={(val) => onLineChange(originalIndex, 'tax_rate', val)} decimals={2} placeholder="19.00" min={0} max={100} style={{ padding: '6px 8px', fontSize: '13px', minWidth: '55px' }} />
                        </td>
                        <td style={{ ...tdStyle, width: '70px' }}>
                          <NumericInput value={item.discount_percent} onChange={(val) => onLineChange(originalIndex, 'discount_percent', val)} decimals={2} placeholder="0.00" min={0} max={100} style={{ padding: '6px 8px', fontSize: '13px', minWidth: '55px' }} />
                        </td>
                        <td style={{ ...tdStyle, width: '80px', textAlign: 'center' }}>
                          <span style={{ color: '#ccc', fontSize: '12px' }}>—</span>
                        </td>
                        <td style={{ ...tdStyle, width: '140px' }}>-</td>
                        <td style={{ ...tdStyle, width: '120px', textAlign: 'right' }}>-</td>
                        <td style={tdStyle}>
                          <button onClick={() => onRemoveLine(originalIndex)} title="Remove item" style={{ background: 'none', border: 'none', padding: '6px', cursor: 'pointer', color: '#dc2626', borderRadius: '4px' }}>
                            <FaTrash size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </>
              ) : (
                /* Standard line items view (no milestone sections) */
                lineItems.map((item, index) => {
                  const lineTotal = calculateLineTotal(item);
                  const offerLineId = getOfferLineId(item);
                  const isRealLine = offerLineId !== null;
                  return (
                    <tr key={index} style={{ borderBottom: '1px solid #e0e3ef' }}>
                      <td style={tdStyle}>{index + 1}</td>
                      <td style={{ ...tdStyle, fontSize: '12px', color: '#666' }}>
                        {item._catalogMeta?.sku || '-'}
                      </td>
                      <td style={{ ...tdStyle, fontSize: '12px' }}>
                        {item._catalogMeta?.kind ? (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 6px',
                              background: '#f0f4ff',
                              borderRadius: '4px',
                              fontSize: '11px',
                              color: '#021a4a',
                            }}
                          >
                            {CatalogService.getKindLabel(item._catalogMeta.kind)}
                          </span>
                        ) : '-'}
                      </td>
                      <td style={{ ...tdStyle, minWidth: '280px' }}>
                        {item.name ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span
                              onClick={() => isRealLine && onScopeDrawerOpen({ id: offerLineId!, name: item.name })}
                              style={{
                                fontWeight: 600,
                                color: '#021a4a',
                                cursor: isRealLine ? 'pointer' : 'default',
                                textDecoration: isRealLine ? 'underline' : 'none',
                                textDecorationStyle: 'dotted',
                                textUnderlineOffset: '3px',
                              }}
                              title={isRealLine ? 'Klicken für Details & Subtasks' : undefined}
                            >
                              {item.name}
                            </span>
                            <CatalogItemPicker
                              onSelect={(catalogItem) => onCatalogSelect(index, catalogItem)}
                              placeholder="Artikel suchen..."
                              compact
                            />
                          </div>
                        ) : (
                          <CatalogItemPicker
                            onSelect={(catalogItem) => onCatalogSelect(index, catalogItem)}
                            placeholder="Artikel auswählen..."
                          />
                        )}
                      </td>
                      <td style={{ ...tdStyle, width: '80px' }}>
                        <NumericInput
                          value={item.quantity}
                          onChange={(val) => onLineChange(index, 'quantity', val)}
                          decimals={2}
                          placeholder="1.00"
                          min={0}
                          style={{ padding: '6px 8px', fontSize: '13px', minWidth: '70px' }}
                        />
                      </td>
                      <td style={{ ...tdStyle, width: '70px' }}>
                        <input
                          type="text"
                          value={item.unit}
                          onChange={(e) => onLineChange(index, 'unit', e.target.value)}
                          style={{ ...inputStyle, padding: '6px 8px', fontSize: '13px', width: '55px' }}
                        />
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          width: '100px',
                          textAlign: 'right',
                          color: hasCatalogPurchasePrice(item) ? '#888' : '#aaa',
                          background: '#fafafa',
                          fontSize: '13px',
                          fontStyle: hasCatalogPurchasePrice(item) ? 'normal' : 'italic',
                          whiteSpace: 'nowrap',
                        }}
                        title={hasCatalogPurchasePrice(item)
                          ? "Based on catalog purchase price - not shown on PDF"
                          : "Estimated (70% of unit price) - not shown on PDF"}
                      >
                        {getPurchasePrice(item) != null
                          ? formatCurrency(getPurchasePrice(item)!)
                          : '-'}
                        {!hasCatalogPurchasePrice(item) && getPurchasePrice(item) != null && (
                          <span style={{ fontSize: '10px', marginLeft: '2px' }}>*</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, width: '80px', background: '#fafafa' }}>
                        {getPurchasePrice(item) != null ? (
                          <NumericInput
                            value={calculateMarkup(item)}
                            onChange={(val) => onMarkupChange(index, val)}
                            decimals={2}
                            placeholder="1.00"
                            min={0}
                            style={{ padding: '6px 8px', fontSize: '13px', minWidth: '65px' }}
                          />
                        ) : (
                          <span style={{ color: '#888', fontSize: '13px' }}>-</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, width: '110px' }}>
                        <NumericInput
                          value={item.unit_price}
                          onChange={(val) => onLineChange(index, 'unit_price', val)}
                          decimals={2}
                          placeholder="0.00"
                          min={0}
                          style={{ padding: '6px 8px', fontSize: '13px', minWidth: '95px' }}
                        />
                      </td>
                      <td style={{ ...tdStyle, width: '70px' }}>
                        <NumericInput
                          value={item.tax_rate}
                          onChange={(val) => onLineChange(index, 'tax_rate', val)}
                          decimals={2}
                          placeholder="19.00"
                          min={0}
                          max={100}
                          style={{ padding: '6px 8px', fontSize: '13px', minWidth: '55px' }}
                        />
                      </td>
                      <td style={{ ...tdStyle, width: '70px' }}>
                        <NumericInput
                          value={item.discount_percent}
                          onChange={(val) => onLineChange(index, 'discount_percent', val)}
                          decimals={2}
                          placeholder="0.00"
                          min={0}
                          max={100}
                          style={{ padding: '6px 8px', fontSize: '13px', minWidth: '55px' }}
                        />
                      </td>
                      {/* Scope column */}
                      <td style={{ ...tdStyle, width: '80px', textAlign: 'center' }}>
                        {isRealLine ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <span
                              data-testid={`offer-line-scope-count-${offerLineId}`}
                              style={{
                                fontSize: '12px',
                                color: (item.scope_items_count ?? 0) > 0 ? '#166534' : '#9ca3af',
                                fontWeight: (item.scope_items_count ?? 0) > 0 ? 600 : 400,
                              }}
                            >
                              {(item.scope_items_count ?? 0) > 0 ? `🧾 ${item.scope_items_count}` : '—'}
                            </span>
                            <button
                              data-testid={`offer-line-scope-open-${offerLineId}`}
                              onClick={() => onScopeDrawerOpen({ id: offerLineId!, name: item.name })}
                              title="Scope öffnen"
                              style={{
                                background: 'none',
                                border: '1px solid #e0e3ef',
                                borderRadius: '4px',
                                padding: '2px 6px',
                                cursor: 'pointer',
                                fontSize: '10px',
                                color: '#666',
                              }}
                            >
                              ✎
                            </button>
                          </div>
                        ) : (
                          <span style={{ color: '#ccc', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, width: '120px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {formatCurrency(lineTotal.net)}
                      </td>
                      <td style={tdStyle}>
                        <button
                          onClick={() => onRemoveLine(index)}
                          title="Remove item"
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: '6px',
                            cursor: 'pointer',
                            color: '#dc2626',
                            borderRadius: '4px',
                          }}
                        >
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>


      {/* Totals */}
      {lineItems.filter((li) => !isLineEmpty(li)).length > 0 && (
        <div
          style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '2px solid #e0e3ef',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <div style={{ width: '300px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Subtotal net:</span>
              <span style={{ fontWeight: 600 }}>{formatCurrency(totals.subtotalNet)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>VAT:</span>
              <span style={{ fontWeight: 600 }}>{formatCurrency(totals.totalTax)}</span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: '12px',
                borderTop: '2px solid #021a4a',
                fontSize: '18px',
              }}
            >
              <span style={{ fontWeight: 700, color: '#021a4a' }}>Total gross:</span>
              <span style={{ fontWeight: 700, color: '#021a4a' }}>{formatCurrency(totals.totalGross)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
